import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { ResponseDto } from "@mimber/schema"
import { taxRatesKeys, taxRateAssignmentsKeys } from "../tax-rates.utils"

/**
 * Organization-specific hook for deleting a tax rate.
 * The server rejects deletion with a 400 if the rate is currently assigned
 * to a revenue type — reassign or clear the assignment first.
 * Can only be used in mimber-organization app.
 */
export const useDeleteTaxRate = (
  options?: UseMutationOptions<ResponseDto<void>, Error, string>
): UseMutationResult<ResponseDto<void>, Error, string> => {
  guardAppUsage("mimber-organization", "useDeleteTaxRate")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (uuid: string): Promise<ResponseDto<void>> => {
      const response = await server.delete<ResponseDto<void>>(
        `tax-rates/${uuid}`
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: taxRatesKeys.findAll() })
      queryClient.invalidateQueries({
        queryKey: taxRateAssignmentsKeys.getAssignments(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
